import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TableVirtuoso, Virtuoso } from 'react-virtuoso';
import { LocateFixed, ChevronRight, ChevronDown } from 'lucide-react';
import { apiFetch } from '@/lib/api.js';
import { useParams, useNavigate } from 'react-router-dom';
import { useMediaQuery } from '../lib/useMediaQuery.js';
import {
  PublicationRollup, LIFECYCLE_LABEL, LIFECYCLE_BADGE, DriftDot, DriftLegend, RowActions,
} from '../lib/events.jsx';
import { LoadingState, ErrorState } from '../lib/Spinner.jsx';

// Phase 1a — sortable/filterable table of events for the current tenant.
// Calendar + Kanban land in Phase 1b. Event detail / edit in Phase 2.

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// Door/start time in venue-local 12-hour format. Mirrors DistributionListings.jsx fmtTime;
// input is a timestamptz instant (doors_at/start_at), not the announce_time string.
function fmtTime(instant, tz) {
  if (!instant) return '';
  return new Date(instant).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz || 'America/New_York',
  });
}

// Inline version timeline shown when a list row's chevron is expanded.
// Compact label for a version's content (event_name — talent from its snapshot).
function versionLabel(v, fallback) {
  const s = v?.snapshot || {};
  const name = s.event_name || fallback?.event_name;
  const talent = s.talent || fallback?.talent;
  return [name, talent && talent !== name ? talent : null].filter(Boolean).join(', ') || '(no content)';
}

// One version rendered as a listing-style row aligned to the table columns.
// Cells only (no <tr>): the row element is provided by TableVirtuoso's
// TableRow component, which owns row-level classes per item type.
function VersionCells({ event, version, live, pending, platforms = [], onEdit, onPublish }) {
  const s = version.snapshot || {};
  const eventName = s.event_name || event.event_name || '';
  const talent = s.talent || '';
  const badge = live ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
    : pending ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'bg-muted text-muted-foreground';
  const tag = live ? '● current' : pending ? 'pending announce' : 'past';
  return (
    <>
      <td className="px-1 py-1.5 text-center font-mono text-muted-foreground">v{version.version_number}</td>
      <td></td>
      <td className="px-2 py-1.5">{s.flyer_image_url ? <img src={s.flyer_image_url} alt="" className="w-7 h-7 rounded object-cover" /> : null}</td>
      <td className="px-3 py-1.5 whitespace-nowrap text-muted-foreground">
        <div>{formatDate(s.date || event.date)}</div>
        {s.doors_at && <div className="text-[10px]">{fmtTime(s.doors_at, event.venue_timezone)}</div>}
      </td>
      <td className="px-3 py-1.5">{eventName}</td>
      <td className="px-3 py-1.5">{talent && talent !== eventName ? talent : <span className="text-muted-foreground">—</span>}</td>
      <td className="px-3 py-1.5 hidden lg:table-cell"><AnnounceCell announceDate={s.announce_date} announceTime={s.announce_time} /></td>
      <td className="px-3 py-1.5"><span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${badge}`}>{tag}{version.kind ? ` · ${version.kind}` : ''}</span></td>
      <td className="px-3 py-1.5 hidden md:table-cell text-muted-foreground truncate">{platforms.length ? platforms.join(', ') : ''}</td>
      <td className="px-3 py-1.5 hidden md:table-cell"></td>
      <td></td>
      <td className="px-3 py-1.5">
        <span className="inline-flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onEdit(event, version); }} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">Edit</button>
          {pending && onPublish && <button onClick={(e) => { e.stopPropagation(); onPublish(event); }} className="text-[10px] px-1.5 py-0.5 border border-primary bg-primary text-primary-foreground rounded hover:opacity-90">Publish</button>}
        </span>
      </td>
    </>
  );
}
// (Version-chain flattening — loading/error/none states, live/pending
// resolution, the Add-version row — lives in the items useMemo in the list
// component: Virtuoso needs one item per row.)

// LIFECYCLE_LABEL / LIFECYCLE_BADGE imported from lib/events.jsx — single
// source of truth across List/Calendar/Kanban/EventDetail.

function Thumbnail({ url, talent }) {
  if (url) {
    return <img src={url} alt="" loading="lazy" className="w-10 h-10 rounded object-cover bg-muted" />;
  }
  const initials = (talent || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join('') || '?';
  // Deterministic color from talent string so the same artist always gets the same swatch.
  const hue = Math.abs((talent || '').split('').reduce((a, c) => a * 31 + c.charCodeAt(0), 7)) % 360;
  return (
    <div
      className="w-10 h-10 rounded flex items-center justify-center text-xs font-semibold text-white"
      style={{ background: `hsl(${hue} 55% 45%)` }}
    >{initials}</div>
  );
}

// Shows whether the event has been distributed to platforms (UrVenue etc.)
// via the publisher pipeline. Independent of publication status — that
// tracks per-platform sync; this tracks "did the placeholder/announce push
// actually fire and complete."
// Platforms column: one chip per platform, colored by that platform's publication state.
const PLATFORM_TONE = {
  published:   'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  manual_only: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  pending:     'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  failed:      'bg-red-500/15 text-red-700 dark:text-red-300',
};
function PlatformChips({ publications }) {
  const pubs = (publications || []).filter((p) => !p.platform.startsWith('monday')); // monday_* live in Dispatch
  if (!pubs.length) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {pubs.map((p) => (
        <span key={p.platform} title={`${p.platform}: ${p.status}`}
          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${PLATFORM_TONE[p.status] || 'bg-muted text-muted-foreground'}`}>
          {p.platform}
        </span>
      ))}
    </div>
  );
}

// Dispatch column: jobs pushed to agencies via Monday/Asana (Meta ad, video assets).
// Pill color reflects the synced Monday job status (job_status); falls back to a
// neutral "dispatched" tone when no status has synced yet (e.g. before a token is set).
const DISPATCH_LABEL = { monday_ad: 'Meta', monday_video_promo: 'Video promo', monday_video_ad: 'Video ad' };
function jobTone(jobStatus) {
  if (!jobStatus) return null;
  const s = jobStatus.toLowerCase();
  if (/done|complete|approv|live|ready|deliver/.test(s)) return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400';
  if (/stuck|block|revis|reject|hold|late/.test(s))      return 'bg-red-500/15 text-red-700 dark:text-red-300';
  if (/work|progress|review|build|design|prod/.test(s))  return 'bg-amber-500/15 text-amber-700 dark:text-amber-400';
  return 'bg-muted text-muted-foreground'; // not started / unknown
}
function DispatchChips({ publications }) {
  const jobs = (publications || []).filter((p) => p.platform.startsWith('monday'));
  if (!jobs.length) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {jobs.map((p) => (
        <span key={p.platform} title={`${DISPATCH_LABEL[p.platform] || p.platform}${p.job_status ? ': ' + p.job_status : ' (dispatched)'}`}
          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${jobTone(p.job_status) || 'bg-blue-500/15 text-blue-700 dark:text-blue-300'}`}>
          {DISPATCH_LABEL[p.platform] || p.platform}
        </span>
      ))}
    </div>
  );
}

function DistributionBadge({ count, total, lastKind }) {
  const n = Number(count) || 0;
  const t = Number(total) || 0;
  if (t === 0) return null;             // No platform_sync rows → not registered for distribution
  if (n === 0) {
    // Queued: sync rows exist (event linked to template w/ publish_defaults)
    // but nothing has actually been pushed yet.
    return (
      <span title={`${t} platform(s) queued, none pushed yet`}
            className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-400">
        ⏳ queued
      </span>
    );
  }
  const label = lastKind === 'placeholder' ? 'placeholder out'
             : lastKind === 'announced'   ? 'announced'
             : lastKind === 'cancellation_revert' ? 'reverted'
             : 'edit pushed';
  const tone = lastKind === 'announced'
    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
    : 'bg-blue-500/10 text-blue-700 dark:text-blue-300';
  return (
    <span title={`${n} of ${t} platform(s) distributed · latest: ${lastKind}`}
          className={`text-[10px] px-1.5 py-0.5 rounded ${tone}`}>
      ⇧ {label}{n < t ? ` (${n}/${t})` : ''}
    </span>
  );
}

function AnnounceCell({ announceDate, announceTime }) {
  if (!announceDate && !announceTime) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  const dateLabel = announceDate
    ? new Date(announceDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
    : null;
  const timeLabel = announceTime ? formatTimeShort(announceTime) : null;

  let chip = null;
  if (announceDate) {
    const today = new Date(); today.setHours(0,0,0,0);
    const ad = new Date(announceDate);
    const days = Math.round((ad - today) / 86400000);
    if (days >= 0 && days <= 14) {
      chip = (
        <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] bg-amber-500/10 text-amber-700 dark:text-amber-400">
          {days === 0 ? 'today' : `in ${days}d`}
        </span>
      );
    }
  }

  // Two stacked lines (same idiom as the Date column) so the cell fits its
  // narrow fixed column instead of one nowrap line bleeding into Status.
  return (
    <div>
      {dateLabel && <div className="font-medium">{dateLabel}</div>}
      {(timeLabel || chip) && (
        <div className="text-xs text-muted-foreground">{timeLabel}{chip}</div>
      )}
    </div>
  );
}

function formatTimeShort(t) {
  // t is "HH:MM:SS" or "HH:MM"
  const [hh, mm] = t.split(':').map((x) => parseInt(x, 10));
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
}

// Status reflects the booking→announce flow (mirrors the version lifecycle):
// Placeholder (nothing booked) → Booked (talent set, pending announce) → Live (announced).
// Derived from lifecycle_status + talent so it always matches the version chevron.
function statusKey(e) {
  const lc = e.lifecycle_status;
  if (lc === 'past') return 'past';
  if (lc === 'cancelled') return 'cancelled';
  if (lc === 'announced') return 'live';
  const booked = e.talent && !/^(tbd|tba|placeholder)$/i.test(String(e.talent).trim());
  return booked ? 'booked' : 'placeholder';
}
const STATUS_DISPLAY = {
  placeholder: { label: 'Placeholder', tone: 'bg-muted text-muted-foreground' },
  booked:      { label: 'Booked',      tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  live:        { label: 'Live',        tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  cancelled:   { label: 'Cancelled',   tone: 'bg-destructive/15 text-destructive' },
  past:        { label: 'Past',        tone: 'bg-muted text-muted-foreground italic' },
};
function LifecycleStatusBadge({ event }) {
  const d = STATUS_DISPLAY[statusKey(event)];
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${d.tone}`}>{d.label}</span>;
}

export default function EventsList() {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  const openEditor = (e) => navigate(`/${tenantSlug}/event/${e.id}`);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [selected, setSelected] = useState(new Set());
  const [bulking, setBulking] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  // Chevron-expand → lazy-load the event's version timeline (detail endpoint already
  // returns `versions` + `platform_sync`), so we don't bloat the 800-row list query.
  const [expanded, setExpanded] = useState(new Set());
  const [versionData, setVersionData] = useState({}); // id -> {loading, versions, platformSync, error}
  const toggleExpand = (e) => {
    setExpanded((s) => { const n = new Set(s); n.has(e.id) ? n.delete(e.id) : n.add(e.id); return n; });
    if (versionData[e.id]) return;
    setVersionData((d) => ({ ...d, [e.id]: { loading: true } }));
    apiFetch(`/api/tenants/${tenantSlug}/events/${e.id}`)
      .then((r) => r.json())
      .then((d) => setVersionData((v) => ({ ...v, [e.id]: { loading: false, versions: d.versions || [], platformSync: d.platform_sync || [] } })))
      .catch(() => setVersionData((v) => ({ ...v, [e.id]: { loading: false, error: true } })));
  };

  // Add-booking quick form → POST /book (fills the date's placeholder or creates from template).
  const [showBook, setShowBook] = useState(false);
  const [bookForm, setBookForm] = useState({ talent: '', date: '', announce_date: '' });
  const [bookBusy, setBookBusy] = useState(false);
  const [bookErr, setBookErr] = useState(null);
  const submitBook = async () => {
    if (!bookForm.talent.trim() || !bookForm.date) { setBookErr('Artist name and date are required.'); return; }
    setBookBusy(true); setBookErr(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/events/book`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: bookForm.date, talent: bookForm.talent.trim(), announce_date: bookForm.announce_date || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(
        d.error === 'no_template_for_date' ? 'No residency template for that date. Create a placeholder first.'
        : d.error === 'multiple_events_on_date' ? 'Multiple events that night, open the date to pick a slot.'
        : (d.detail || d.error || 'book_failed'));
      setShowBook(false); setBookForm({ talent: '', date: '', announce_date: '' });
      refetch();
    } catch (e) { setBookErr(e.message); }
    finally { setBookBusy(false); }
  };
  const openBookFor = (e) => {
    const d = typeof e.date === 'string' ? e.date.slice(0, 10) : new Date(e.date).toISOString().slice(0, 10);
    setBookErr(null);
    setBookForm({ talent: '', date: d, announce_date: '' });
    setShowBook(true);
  };
  // Publish the pending version: announce (pre-announce) or push the edit (already announced).
  const publishEvent = async (e) => {
    const path = (e.lifecycle_status || e.status) === 'announced' ? 'sync-platforms' : 'announce';
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/events/${e.id}/${path}?authorize_writes=1`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || 'publish_failed');
      setVersionData((v) => { const n = { ...v }; delete n[e.id]; return n; }); // force re-fetch of versions
      setExpanded((s) => { const n = new Set(s); n.delete(e.id); return n; });
      refetch();
    } catch (err) { window.alert('Publish failed: ' + err.message); }
  };
  // Re-pull an event's versions into the expanded panel (after edit/add).
  const refetchVersions = (id) => {
    apiFetch(`/api/tenants/${tenantSlug}/events/${id}`).then((r) => r.json())
      .then((d) => setVersionData((v) => ({ ...v, [id]: { loading: false, versions: d.versions || [], platformSync: d.platform_sync || [] } })))
      .catch(() => {});
  };
  const [editVer, setEditVer] = useState(null); // { eventId, versionId, num, form }
  // Version "Edit" opens the FULL editor scoped to that version (not the basic modal).
  const openVersionEdit = (e, v) => navigate(`/${tenantSlug}/event/${e.id}?version=${v.id}&edit=1`);
  const saveVersionEdit = async () => {
    const { eventId, versionId, form } = editVer;
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}/versions/${versionId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || 'save_failed');
      setEditVer(null); refetchVersions(eventId); refetch();
    } catch (e) { window.alert('Save failed: ' + e.message); }
  };
  const addVersion = async (e) => {
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/events/${e.id}/versions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const v = await r.json();
      if (!r.ok) throw new Error(v.error || v.detail || 'add_failed');
      refetchVersions(e.id);
      openVersionEdit(e, { id: v.id, version_number: v.version_number, snapshot: {} });
    } catch (err) { window.alert('Add version failed: ' + err.message); }
  };

  const [events, setEvents] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ status: 'all', text: '' });
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('asc');
  const listWrapRef = useRef(null);
  const [listH, setListH] = useState(null);

  // Refetch in place — replaces window.location.reload() so bulk actions +
  // row-level actions don't blow away filter / selection / scroll.
  // ?all=1 → include past events so the now-anchored timeline can scroll
  // downward into history (upcoming above, past below).
  const refetch = async () => {
    const r = await apiFetch(`/api/tenants/${tenantSlug}/events?all=1`);
    const data = await r.json();
    if (data.error) throw new Error(data.error);
    setEvents(data.events);
    setCount(data.count);
  };

  const load = () => {
    setLoading(true);
    setError(null);
    refetch()
      .then(() => setLoading(false))
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug]);

  const runBulk = async (path, body) => {
    setBulking(true);
    setBulkError(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      // Refetch instead of reload — keeps filter/scroll/search intact.
      await refetch();
      setSelected(new Set());
    } catch (e) {
      setBulkError(e.message);
    } finally {
      setBulking(false);
    }
  };

  // Memoized so the sort memo below doesn't recompute every render. Text match
  // spans talent + genre + status so the filter box is useful across 800+ rows.
  const visible = useMemo(() => events.filter((e) => {
    if (filter.status !== 'all' && statusKey(e) !== filter.status) return false;
    if (filter.text) {
      const q = filter.text.toLowerCase();
      const hay = `${e.talent || ''} ${e.genre || ''} ${e.lifecycle_status || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [events, filter.status, filter.text]);

  // Sortable like the widget's "By event" table. Default = chronological
  // (date asc): past at top, future at bottom, first upcoming parked at the top.
  const todayStr = new Date().toLocaleDateString('en-CA'); // local YYYY-MM-DD
  const sortedEvents = useMemo(() => {
    const cmp = (a, b) => {
      let av, bv;
      if (sortKey === 'event') { av = (a.talent || '').toLowerCase(); bv = (b.talent || '').toLowerCase(); }
      else if (sortKey === 'event_name') { av = (a.event_name || '').toLowerCase(); bv = (b.event_name || '').toLowerCase(); }
      else if (sortKey === 'status') { av = a.lifecycle_status || ''; bv = b.lifecycle_status || ''; }
      else if (sortKey === 'announce') { av = a.announce_date || '9999'; bv = b.announce_date || '9999'; }
      else { av = a.date || '9999'; bv = b.date || '9999'; } // 'date'
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      // Same day → order by door/start time (earliest show first), then talent name.
      // Rows with no time sort after timed ones. Independent of sortDir (doors intuition).
      const at = a.start_at || a.doors_at || '';
      const bt = b.start_at || b.doors_at || '';
      if (at && bt && at !== bt) return at < bt ? -1 : 1;
      if (at && !bt) return -1;
      if (!at && bt) return 1;
      return (a.talent || '').localeCompare(b.talent || '');
    };
    return [...visible].sort(cmp);
  }, [visible, sortKey, sortDir]);

  const chronological = sortKey === 'date' && sortDir === 'asc';
  const firstUpcomingIdx = useMemo(() => (
    chronological ? sortedEvents.findIndex((e) => (e.date || '') >= todayStr) : -1
  ), [sortedEvents, chronological, todayStr]);


  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'event' ? 'asc' : key === 'date' ? 'asc' : 'desc'); }
  };
  const sortIndicator = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');
  // Keyboard + screen-reader props for the sortable column headers.
  const sortProps = (key) => ({
    onClick: () => onSort(key),
    onKeyDown: (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onSort(key); } },
    role: 'button',
    tabIndex: 0,
    'aria-sort': sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none',
  });

  // ---- Virtualization: react-virtuoso measures every row's REAL height ----
  // (ResizeObserver + exact offsets), so variable-height rows — conditional
  // time/genre sub-lines, expanded version chains — never cause the spacer
  // drift and scroll jumping the hand-rolled fixed-slot windowing had.
  // Virtuoso's contract is one item = one row, so expanded version chains are
  // flattened into the item list as their own rows.
  const items = useMemo(() => {
    const out = [];
    sortedEvents.forEach((e, idx) => {
      out.push({ type: 'event', key: e.id, e, idx });
      if (isDesktop && expanded.has(e.id)) {
        const d = versionData[e.id];
        if (!d || d.loading) out.push({ type: 'vinfo', key: `${e.id}:vload`, text: 'Loading versions…' });
        else if (d.error) out.push({ type: 'vinfo', key: `${e.id}:verr`, text: 'Failed to load versions.', tone: 'text-destructive' });
        else {
          const versions = [...(d.versions || [])].sort((a, b) => a.version_number - b.version_number);
          const sync = d.platformSync || [];
          const liveNum = Math.max(0, ...sync.map((s) => Number(s.live_version_number) || 0));
          if (!versions.length) out.push({ type: 'vinfo', key: `${e.id}:vnone`, text: 'No versions yet.' });
          for (const v of versions) {
            const n = Number(v.version_number);
            const live = liveNum > 0 && n === liveNum;
            out.push({
              type: 'version', key: `v:${v.id}`, e, v, live,
              pending: n > liveNum,
              platforms: live ? sync.filter((s) => s.live_version_id === v.id).map((s) => s.platform) : [],
            });
          }
          out.push({ type: 'vadd', key: `${e.id}:vadd`, e });
        }
      }
    });
    return out;
  }, [sortedEvents, expanded, versionData, isDesktop]);
  // The anchor row's position within `items` (expansions above it shift it).
  const firstUpcomingItemIdx = useMemo(
    () => items.findIndex((it) => it.type === 'event' && it.idx === firstUpcomingIdx),
    [items, firstUpcomingIdx],
  );

  const [showJump, setShowJump] = useState(false);
  const virtuosoRef = useRef(null);
  const goHome = (smooth) => {
    if (!virtuosoRef.current) return;
    virtuosoRef.current.scrollToIndex(
      firstUpcomingItemIdx >= 0
        ? { index: firstUpcomingItemIdx, align: 'start', behavior: smooth ? 'smooth' : 'auto' }
        : { index: Math.max(0, items.length - 1), align: 'end' },
    );
    setShowJump(false);
  };
  const onRangeChanged = ({ startIndex, endIndex }) => {
    setShowJump(chronological && firstUpcomingItemIdx >= 0
      && (firstUpcomingItemIdx < startIndex || firstUpcomingItemIdx > endIndex));
  };
  // Park on "today" at mount (initialTopMostItemIndex covers first paint) and
  // whenever the user returns to the chronological default sort.
  const initialTop = firstUpcomingItemIdx >= 0
    ? { index: firstUpcomingItemIdx, align: 'start' }
    : Math.max(0, items.length - 1);
  useEffect(() => {
    if (!loading && chronological) goHome(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chronological, loading, isDesktop]);
  // Size the list frame to fill the viewport (cheap now — content-visibility means only visible rows lay out).
  useLayoutEffect(() => {
    const calc = () => {
      const el = listWrapRef.current;
      if (!el) return;
      const next = Math.max(260, Math.round(window.innerHeight - el.getBoundingClientRect().top - 16));
      setListH((prev) => (prev === next ? prev : next));
    };
    calc();
    let raf = 0;
    const onResize = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(calc); };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); cancelAnimationFrame(raf); };
  }, [loading, selected.size > 0, filter.status, filter.text]);

  if (loading) return <LoadingState label="Loading events…" />;
  if (error) return <ErrorState error={error} onRetry={load} />;

  const filterActive = filter.status !== 'all' || filter.text.trim() !== '';
  const filterDesc = [
    filter.status !== 'all' ? (STATUS_DISPLAY[filter.status]?.label || filter.status) : null,
    filter.text.trim() ? `“${filter.text.trim()}”` : null,
  ].filter(Boolean).join(' + ');

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1 className="text-2xl font-semibold">Events</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setBookErr(null); setShowBook(true); }}
            className="px-3 py-1 text-xs rounded-md bg-primary text-primary-foreground hover:opacity-90"
            title="Book a performer for a date (Queued, not announced)"
          >
            + Add booking
          </button>
          <a
            href={`/api/tenants/${tenantSlug}/events.csv`}
            className="px-3 py-1 text-xs border border-input rounded-md hover:bg-accent"
            title="Download all upcoming events as CSV"
          >
            Export CSV
          </a>
          <a
            href={`/api/tenants/${tenantSlug}/events.csv?all=1`}
            className="px-3 py-1 text-xs border border-input rounded-md hover:bg-accent"
            title="Download all events (incl. past) as CSV"
          >
            All
          </a>
          <div className="text-sm text-muted-foreground">
            {visible.length} of {count}
          </div>
        </div>
      </div>

      {showBook && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowBook(false)}>
          <div className="bg-card border border-border rounded-lg p-5 w-80 space-y-3" onClick={(ev) => ev.stopPropagation()}>
            <h2 className="text-lg font-semibold">Add booking</h2>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Artist / talent</label>
              <input autoFocus value={bookForm.talent} onChange={(ev) => setBookForm((f) => ({ ...f, talent: ev.target.value }))}
                onKeyDown={(ev) => { if (ev.key === 'Enter') submitBook(); }}
                className="w-full px-2 py-1.5 border border-input rounded-md bg-background text-sm" placeholder="e.g. Lil Baby" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Date</label>
              <input type="date" value={bookForm.date} onChange={(ev) => setBookForm((f) => ({ ...f, date: ev.target.value }))}
                className="w-full px-2 py-1.5 border border-input rounded-md bg-background text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Announce date (optional)</label>
              <input type="date" value={bookForm.announce_date} onChange={(ev) => setBookForm((f) => ({ ...f, announce_date: ev.target.value }))}
                className="w-full px-2 py-1.5 border border-input rounded-md bg-background text-sm" />
            </div>
            {bookErr && <div className="text-xs text-destructive">{bookErr}</div>}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowBook(false)} className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent">Cancel</button>
              <button onClick={submitBook} disabled={bookBusy} className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">{bookBusy ? 'Booking…' : 'Book (Queued)'}</button>
            </div>
            <p className="text-[11px] text-muted-foreground">Books into Queued, not announced. Fills the date's placeholder if one exists, else creates from the residency template.</p>
          </div>
        </div>
      )}

      {editVer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setEditVer(null)}>
          <div className="bg-card border border-border rounded-lg p-5 w-80 space-y-3" onClick={(ev) => ev.stopPropagation()}>
            <h2 className="text-lg font-semibold">Edit version v{editVer.num}</h2>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Event name</label>
              <input value={editVer.form.event_name} onChange={(ev) => setEditVer((s) => ({ ...s, form: { ...s.form, event_name: ev.target.value } }))}
                className="w-full px-2 py-1.5 border border-input rounded-md bg-background text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Talent</label>
              <input value={editVer.form.talent} onChange={(ev) => setEditVer((s) => ({ ...s, form: { ...s.form, talent: ev.target.value } }))}
                className="w-full px-2 py-1.5 border border-input rounded-md bg-background text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Announce date</label>
              <input type="date" value={editVer.form.announce_date} onChange={(ev) => setEditVer((s) => ({ ...s, form: { ...s.form, announce_date: ev.target.value } }))}
                className="w-full px-2 py-1.5 border border-input rounded-md bg-background text-sm" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setEditVer(null)} className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent">Cancel</button>
              <button onClick={saveVersionEdit} className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90">Save</button>
            </div>
            <p className="text-[11px] text-muted-foreground">Edits a draft version. The live (current) version stays frozen, editing it creates a new draft.</p>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Filter by talent…"
          className="flex-1 px-3 py-1.5 rounded-md border border-input bg-background text-sm"
          value={filter.text}
          onChange={(e) => setFilter((f) => ({ ...f, text: e.target.value }))}
        />
        <select
          className="px-3 py-1.5 rounded-md border border-input bg-background text-sm"
          value={filter.status}
          onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
        >
          <option value="all">All statuses</option>
          <option value="placeholder">Placeholder</option>
          <option value="booked">Booked</option>
          <option value="live">Live</option>
          <option value="cancelled">Cancelled</option>
          <option value="past">Past</option>
        </select>
      </div>

      {/* Mobile sort control, the desktop sorts via column headers. */}
      {!isDesktop && (
        <div className="flex gap-2 mb-3 items-center">
          <label className="text-xs text-muted-foreground flex-shrink-0">Sort</label>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="flex-1 px-3 py-1.5 rounded-md border border-input bg-background text-sm"
            aria-label="Sort by"
          >
            <option value="date">Date</option>
            <option value="event">Talent</option>
            <option value="status">Status</option>
            <option value="announce">Announce</option>
          </select>
          <button
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            className="px-3 py-1.5 rounded-md border border-input bg-background text-sm"
            aria-label={`Sort ${sortDir === 'asc' ? 'ascending' : 'descending'}, toggle`}
          >{sortDir === 'asc' ? '↑' : '↓'}</button>
        </div>
      )}

      <div className="flex justify-end mb-2">
        <DriftLegend />
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-2 mb-2 p-2 bg-accent rounded-md text-sm">
          <span className="font-medium">{selected.size} selected</span>
          {bulkError && <span className="text-destructive">{bulkError}</span>}
          <div className="flex-1" />
          <select
            disabled={bulking}
            onChange={(e) => {
              if (!e.target.value) return;
              runBulk('bulk-update', { ids: [...selected], updates: { status: e.target.value } });
              e.target.value = '';
            }}
            className="px-2 py-1 text-xs border border-input rounded-md bg-background"
          >
            <option value="">Set status…</option>
            <option value="draft">Draft</option>
            <option value="sized_up">Sized up</option>
            <option value="pending_announce">Pending announce</option>
            <option value="announced">Announced</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            disabled={bulking}
            onClick={() => { if (confirm(`Move ${selected.size} event${selected.size === 1 ? '' : 's'} to Recently Deleted? They stay recoverable for 30 days.`)) runBulk('bulk-delete', { ids: [...selected] }); }}
            className="px-2 py-1 text-xs border border-destructive text-destructive rounded-md hover:bg-destructive/10"
          >Delete</button>
          <button
            onClick={() => setSelected(new Set())}
            className="px-2 py-1 text-xs border border-input rounded-md hover:bg-background"
          >Clear</button>
        </div>
      )}

      {chronological && (
        <div className="text-[10px] text-muted-foreground mb-1.5">
          Scrolled to today. Scroll up for past events, down for upcoming. Click a column to re-sort.
        </div>
      )}

      <div ref={listWrapRef} className="relative">
      {/* Mobile: stacked cards, virtualized with measured heights; first
          upcoming parked at top. Padding lives per-item (p-2 equivalent). */}
      {!isDesktop && (sortedEvents.length === 0 ? (
        <div className="border border-border rounded-md text-center text-muted-foreground py-6 text-sm">
          {filterActive
            ? <>No events match <span className="font-medium">{filterDesc}</span>. <button onClick={() => setFilter({ status: 'all', text: '' })} className="text-primary hover:underline">Clear</button></>
            : 'No events yet.'}
        </div>
      ) : (
      <Virtuoso
        ref={virtuosoRef}
        style={{ height: listH || 600 }}
        className="border border-border rounded-md"
        data={items}
        computeItemKey={(_, it) => it.key}
        initialTopMostItemIndex={initialTop}
        rangeChanged={onRangeChanged}
        increaseViewportBy={200}
        itemContent={(_, item) => {
          const e = item.e;
          const isFirstUpcoming = item.idx === firstUpcomingIdx;
          return (
          <div className="px-2 pt-2">
          <div
            data-first-upcoming={isFirstUpcoming || undefined}
            onClick={() => openEditor(e)}
            className={`rounded-md p-3 bg-card active:bg-accent/40 cursor-pointer border ${isFirstUpcoming ? 'border-t-2 border-primary/50' : 'border-border'} ${selected.has(e.id) ? 'ring-1 ring-primary/50' : ''} ${e.lifecycle_status === 'past' ? 'grayscale' : ''}`}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                aria-label={`Select ${e.talent}`}
                className="mt-1 flex-shrink-0 w-4 h-4"
                checked={selected.has(e.id)}
                onClick={(ev) => ev.stopPropagation()}
                onChange={() => toggleSelect(e.id)}
              />
              <Thumbnail url={e.flyer_image_url} talent={e.talent} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium truncate">{e.talent}</span>
                  <DriftDot event={e} />
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatDate(e.date)}{(e.start_at || e.doors_at) ? ` · ${fmtTime(e.start_at || e.doors_at, e.venue_timezone)}` : ''}
                  {e.is_placeholder && ' · (placeholder)'}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <LifecycleStatusBadge event={e} />
                  <PlatformChips publications={e.publications} />
                </div>
              </div>
            </div>
            <div className="mt-2 flex justify-end" onClick={(ev) => ev.stopPropagation()}>
              <RowActions event={e} tenantSlug={tenantSlug} onActionComplete={refetch} onBook={openBookFor} />
            </div>
          </div>
          </div>
          );
        }}
      />
      ))}

      {/* Desktop: sortable, sticky-header table, virtualized with MEASURED row
          heights (TableVirtuoso); today parked at top. One item = one <tr>;
          row-level props live in components.TableRow, cells in itemContent. */}
      {isDesktop && (sortedEvents.length === 0 ? (
        <div className="border border-border rounded-md px-3 py-6 text-center text-muted-foreground text-sm">
          {filterActive
            ? <>No events match <span className="font-medium">{filterDesc}</span>. <button onClick={() => setFilter({ status: 'all', text: '' })} className="text-primary hover:underline">Clear filter</button></>
            : 'No events yet.'}
        </div>
      ) : (
      <TableVirtuoso
        ref={virtuosoRef}
        style={{ height: listH || 600 }}
        className="border border-border rounded-md"
        data={items}
        computeItemKey={(_, it) => it.key}
        initialTopMostItemIndex={initialTop}
        rangeChanged={onRangeChanged}
        increaseViewportBy={200}
        components={{
          Table: (props) => <table {...props} className="w-full text-sm table-fixed" />,
          TableRow: ({ item, ...props }) => {
            if (item.type === 'event') {
              const e = item.e;
              const isFirstUpcoming = item.idx === firstUpcomingIdx;
              return (
                <tr
                  {...props}
                  data-first-upcoming={isFirstUpcoming || undefined}
                  className={`hover:bg-accent/30 cursor-pointer ${isFirstUpcoming ? 'border-t-2 border-primary/50' : 'border-t border-border'} ${selected.has(e.id) ? 'bg-primary/5' : ''} ${e.lifecycle_status === 'past' ? 'grayscale' : ''}`}
                  onClick={() => openEditor(e)}
                />
              );
            }
            if (item.type === 'version') return <tr {...props} className="bg-muted/20 border-t border-border/40 text-xs" />;
            if (item.type === 'vadd') return <tr {...props} className="bg-muted/10" />;
            return <tr {...props} className="bg-muted/20" />;
          },
        }}
        fixedHeaderContent={() => (
          <tr className="bg-muted text-left">
            <th className="px-1 py-2 w-5" aria-label="Expand versions"></th>
            <th className="px-3 py-2 w-6">
              <input
                type="checkbox"
                aria-label="Select all visible events"
                checked={visible.length > 0 && visible.every((e) => selected.has(e.id))}
                ref={(el) => { if (el) el.indeterminate = visible.some((e) => selected.has(e.id)) && !visible.every((e) => selected.has(e.id)); }}
                onChange={(e) => {
                  const next = new Set(selected);
                  if (e.target.checked) visible.forEach((ev) => next.add(ev.id));
                  else visible.forEach((ev) => next.delete(ev.id));
                  setSelected(next);
                }}
              />
            </th>
            <th className="px-3 py-2 font-medium w-14"></th>
            <th {...sortProps('date')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted whitespace-nowrap w-[11%]">Date{sortIndicator('date')}</th>
            <th {...sortProps('event_name')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted">Event Name{sortIndicator('event_name')}</th>
            <th {...sortProps('event')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted w-[14%]">Talent{sortIndicator('event')}</th>
            <th {...sortProps('announce')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted hidden lg:table-cell whitespace-nowrap w-[10%]">Announce{sortIndicator('announce')}</th>
            <th {...sortProps('status')} className="px-3 py-2 font-medium cursor-pointer hover:bg-muted whitespace-nowrap w-28">Status{sortIndicator('status')}</th>
            <th className="px-3 py-2 font-medium hidden md:table-cell w-[17%]">Platforms</th>
            <th className="px-3 py-2 font-medium hidden md:table-cell w-[10%]">Dispatch</th>
            <th className="px-3 py-2 font-medium w-6" title="Drift from Universe, see legend above"><span aria-hidden>◆</span></th>
            <th className="px-3 py-2 font-medium w-32">Actions</th>
          </tr>
        )}
        itemContent={(_, item) => {
          if (item.type === 'vinfo') {
            return (<><td></td><td colSpan={11} className={`px-3 py-2 text-xs ${item.tone || 'text-muted-foreground'}`}>{item.text}</td></>);
          }
          if (item.type === 'vadd') {
            return (
              <><td></td><td colSpan={11} className="px-3 py-1.5">
                <button onClick={(ev) => { ev.stopPropagation(); addVersion(item.e); }}
                  className="text-[10px] px-2 py-0.5 border border-dashed border-input rounded hover:bg-accent text-muted-foreground">+ Add version</button>
              </td></>
            );
          }
          if (item.type === 'version') {
            return <VersionCells event={item.e} version={item.v} live={item.live} pending={item.pending} platforms={item.platforms} onEdit={openVersionEdit} onPublish={publishEvent} />;
          }
          const e = item.e;
          const isOpen = expanded.has(e.id);
          return (
            <>
                <td className="px-1 py-2 text-center" onClick={(ev) => ev.stopPropagation()}>
                  <button onClick={() => toggleExpand(e)} aria-label={isOpen ? 'Collapse versions' : 'Expand versions'} className="text-muted-foreground hover:text-foreground">
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  {Number(e.version_count) > 1 && (
                    <div className="text-[9px] text-muted-foreground leading-none mt-0.5"
                      title={`${e.version_count} versions${e.live_version_number ? ` · live v${e.live_version_number}` : ''}`}>
                      {e.live_version_number ? `v${e.live_version_number}/` : ''}{e.version_count}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2" onClick={(ev) => ev.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(e.id)}
                    onChange={() => toggleSelect(e.id)}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Thumbnail url={e.flyer_image_url} talent={e.talent} />
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div>{formatDate(e.date)}</div>
                  {(e.start_at || e.doors_at) && <div className="text-xs text-muted-foreground">{fmtTime(e.start_at || e.doors_at, e.venue_timezone)}</div>}
                </td>
                <td className="px-3 py-2 overflow-hidden">
                  <div className="truncate" title={e.event_name || e.talent || ''}>{e.event_name || e.talent || '—'}</div>
                  {(e.is_placeholder || e.genre) && (
                    <div className="text-xs text-muted-foreground truncate">
                      {e.is_placeholder && '(placeholder)'}{e.is_placeholder && e.genre && ' · '}{e.genre}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 overflow-hidden">
                  {(e.talent && e.talent !== e.event_name)
                    ? <div className="truncate" title={e.talent}>{e.talent}</div>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 hidden lg:table-cell">
                  <AnnounceCell announceDate={e.announce_date} announceTime={e.announce_time} />
                </td>
                <td className="px-3 py-2"><LifecycleStatusBadge event={e} /></td>
                <td className="px-3 py-2 hidden md:table-cell">
                  <PlatformChips publications={e.publications} />
                </td>
                <td className="px-3 py-2 hidden md:table-cell">
                  <DispatchChips publications={e.publications} />
                </td>
                <td className="px-3 py-2 text-center">
                  <DriftDot event={e} />
                </td>
                <td className="px-3 py-2" onClick={(ev) => ev.stopPropagation()}>
                  <RowActions event={e} tenantSlug={tenantSlug} onActionComplete={refetch} onBook={openBookFor} />
                </td>
            </>
          );
        }}
      />
      ))}

      {chronological && showJump && (
        <button
          onClick={() => goHome(true)}
          title="Jump back to today"
          className="absolute bottom-4 right-4 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium shadow-lg hover:opacity-90"
        >
          <LocateFixed size={14} /> Today
        </button>
      )}
      </div>
    </div>
  );
}
